import { FileSource } from '../engine/io/fileutils.js';
import { AddDiv, AddDomElement } from '../engine/viewer/domutils.js';
import { CreateUrlBuilder } from '../engine/parameters/parameterlist.js';
import { ShowMessageDialog } from './dialogs.js';
import { ButtonDialog } from './dialog.js';
import { CopyToClipboard } from './utils.js';
import { HandleEvent } from './eventhandler.js';
import { Loc } from '../engine/core/localization.js';

function GetLocalShareApiUrl ()
{
    let path = window.location.pathname;
    if (path.indexOf ('/website/') !== -1) {
        return '../api/share.php';
    }
    return 'api/share.php';
}

function GetCurrentViewerBaseUrl ()
{
    let url = new URL (window.location.href);
    url.hash = '';
    url.search = '';
    let path = url.pathname;
    if (path.endsWith ('/embed.html')) {
        url.pathname = path.substring (0, path.length - 'embed.html'.length) + 'index.html';
    }
    return url.toString ();
}

function GetModelSharingLink (modelFiles, shareId)
{
    let builder = CreateUrlBuilder ();
    builder.AddModelUrls (modelFiles);
    let hashParameters = builder.GetParameterList ();
    let url = new URL (GetCurrentViewerBaseUrl ());
    if (shareId !== null) {
        url.searchParams.set ('share', shareId);
    }
    return url.toString () + '#' + hashParameters;
}

function GetUrlModelFiles (fileList)
{
    let files = fileList.GetFiles ();
    let modelFiles = [];
    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        let file = files[fileIndex];
        if (file.source === FileSource.Url) {
            modelFiles.push (file.data);
        }
    }
    return modelFiles;
}

function GetUploadableFiles (fileList)
{
    let files = fileList.GetFiles ();
    let uploadableFiles = [];
    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        let file = files[fileIndex];
        if (file.source === FileSource.File) {
            uploadableFiles.push (file.data);
        }
    }
    return uploadableFiles;
}

function UploadModelFilesForSharing (fileList, retentionDays)
{
    let uploadableFiles = GetUploadableFiles (fileList);
    if (uploadableFiles.length === 0) {
        return Promise.reject (new Error (Loc ('There are no local files to upload.')));
    }

    let formData = new FormData ();
    for (let file of uploadableFiles) {
        formData.append ('files[]', file, file.name);
    }
    formData.append ('retentionDays', retentionDays.toString ());

    return fetch (GetLocalShareApiUrl (), {
        method : 'POST',
        body : formData
    }).then ((response) => {
        return response.json ().then ((result) => {
            if (!response.ok || !result.success) {
                throw new Error (result.error || Loc ('Sharing failed.'));
            }
            return result;
        });
    });
}

export function ShowSharingDialog (fileList, settings, viewer)
{
    function AddCopyableTextInput (parentDiv, getText)
    {
        let copyText = Loc ('Copy');
        let copiedText = Loc ('Copied');
        let container = AddDiv (parentDiv, 'ov_dialog_copyable_input');
        let input = AddDomElement (container, 'input', null);
        input.setAttribute ('type', 'text');
        input.readOnly = true;
        let button = AddDiv (container, 'ov_button outline ov_dialog_copyable_input_button', copyText);
        button.addEventListener ('click', () => {
            CopyToClipboard (getText ());
            button.innerHTML = copiedText;
            setTimeout (() => {
                button.innerHTML = copyText;
            }, 2000);
        });
        return input;
    }

    function AddSharingLinkTab (parentDiv, modelFiles, shareId)
    {
        let section = AddDiv (parentDiv, 'ov_dialog_section');
        AddDiv (section, 'ov_dialog_inner_title', Loc ('Sharing Link'));
        let sharingLinkInput = AddCopyableTextInput (section, () => {
            HandleEvent ('model_shared', 'sharing_link');
            return GetModelSharingLink (modelFiles, shareId);
        });
        sharingLinkInput.value = GetModelSharingLink (modelFiles, shareId);
    }

    function OpenShareResultDialog (modelFiles, shareId, expiresAt)
    {
        let dialog = new ButtonDialog ();
        let contentDiv = dialog.Init (Loc ('Share'), [
            {
                name : Loc ('Close'),
                onClick () {
                    dialog.Close ();
                }
            }
        ]);

        if (expiresAt) {
            let expiryDate = new Date (expiresAt);
            AddDiv (contentDiv, 'ov_dialog_submessage', Loc ('This model, link, and its corrections will be deleted automatically on:') + ' ' + expiryDate.toLocaleString ());
        }
        AddSharingLinkTab (contentDiv, modelFiles, shareId);

        dialog.Open ();
        return dialog;
    }

    if (fileList.IsOnlyUrlSource ()) {
        return OpenShareResultDialog (GetUrlModelFiles (fileList), null, null);
    }

    let uploadableFiles = GetUploadableFiles (fileList);
    if (uploadableFiles.length === 0) {
        return ShowMessageDialog (
            Loc ('Sharing Failed'),
            Loc ('Only original uploaded files can be shared. Decompressed files from archives cannot be uploaded for sharing.'),
            null
        );
    }

    let retentionDays = 1;
    let setupDialog = new ButtonDialog ();
    let setupContentDiv = setupDialog.Init (Loc ('Share Model'), [
        {
            name : Loc ('Cancel'),
            onClick () {
                setupDialog.Close ();
            }
        },
        {
            name : Loc ('Create Link'),
            onClick () {
                setupDialog.Close ();
                StartUpload ();
            }
        }
    ]);
    AddDiv (setupContentDiv, 'ov_dialog_message', Loc ('Choose how long the shared model and all correction comments should remain available.'));
    let retentionRow = AddDiv (setupContentDiv, 'ov_dialog_row');
    AddDiv (retentionRow, 'ov_dialog_row_name', Loc ('Time Tenure'));
    let retentionValue = AddDiv (retentionRow, 'ov_dialog_row_value');
    let retentionSelect = AddDomElement (retentionValue, 'select', 'ov_select');
    for (let days of [1, 3, 5]) {
        let option = AddDomElement (retentionSelect, 'option', null, days + (days === 1 ? ' Day' : ' Days'));
        option.value = days.toString ();
    }
    retentionSelect.addEventListener ('change', () => {
        retentionDays = parseInt (retentionSelect.value, 10);
    });
    AddDiv (setupContentDiv, 'ov_dialog_submessage', Loc ('The model is uploaded only after you select Create Link.'));
    setupDialog.Open ();

    function StartUpload ()
    {
        let uploadDialog = new ButtonDialog ();
        let uploadContentDiv = uploadDialog.Init (Loc ('Share'), []);
        AddDiv (uploadContentDiv, 'ov_dialog_message', Loc ('Uploading model files and creating the temporary share link...'));
        uploadDialog.SetCloseable (false);
        uploadDialog.Open ();

        UploadModelFilesForSharing (fileList, retentionDays).then ((result) => {
            uploadDialog.Close ();
            HandleEvent ('model_shared', 'temporary_upload');
            OpenShareResultDialog (result.urls, result.shareId, result.expiresAt);
        }).catch ((error) => {
            uploadDialog.Close ();
            ShowMessageDialog (
                Loc ('Sharing Failed'),
                error.message || Loc ('The model could not be uploaded.'),
                null
            );
        });
    }

    return setupDialog;
}
